export type CsvRow = {
  sku: string;
  fnsku: string;
  asin: string;
  name: string;
  jan: string;
  setCount: string;
  unitCount: string;
  memo: string;
};

// 備考は商品単位のため子行では扱わない
export type CsvChildRow = Omit<CsvRow, "sku" | "fnsku" | "asin" | "memo">;

export type GroupedCsvRow = {
  sku: string;
  fnsku: string;
  asin: string;
  name: string;
  parentJan: string;
  setCount: string;
  unitCount: string;
  memo: string;
  children: CsvChildRow[];
};

export type ParseCsvGroupedResult = {
  groupedRows: GroupedCsvRow[];
  validRows: CsvRow[];
  duplicateRows: CsvRow[];
};

// ダブルクォーテーション付きのCSVを正しくパースする関数
const parseCsvLine = (line: string): string[] => {
  const result: string[] = [];
  let current = "";
  let insideQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (insideQuotes && nextChar === '"') {
        // 連続する "" はエスケープされた " と解釈
        current += '"';
        i++; // skip next quote
      } else {
        insideQuotes = !insideQuotes;
      }
    } else if (char === "," && !insideQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
};

export const parseCsv = async (file: File): Promise<ParseCsvGroupedResult> => {
  const text = await file.text();
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== "");
  const [, ...dataLines] = lines; // ヘッダーは無視

  const groups = new Map<string, GroupedCsvRow>();
  const seenKeys = new Set<string>();

  const validRows: CsvRow[] = [];
  const duplicateRows: CsvRow[] = [];

  for (const line of dataLines) {
    const cols = parseCsvLine(line);
    if (cols.length < 7) continue;

    const row: CsvRow = {
      sku: cols[0].trim(),
      fnsku: cols[1].trim(),
      asin: cols[2].trim(),
      name: cols[3].trim(),
      jan: cols[4].trim(),
      setCount: cols[5].trim(),
      unitCount: cols[6].trim(),
      // 備考は任意列。旧フォーマット（7列）のCSVも読み込めるようにする
      memo: cols[7]?.trim() ?? "",
    };

    const dupKey = `${row.sku}|${row.fnsku}|${row.asin}|${row.jan}`;
    const groupKey = `${row.sku}|${row.fnsku}|${row.asin}`;

    if (seenKeys.has(dupKey)) {
      duplicateRows.push(row);
      continue;
    }

    validRows.push(row);
    seenKeys.add(dupKey);

    if (row.jan === "0" || row.jan === "") {
      // 親行。子行が親行より先に現れる場合があるため、
      // 収集済みの子行を引き継いだ上で親の値を上書きする。
      groups.set(groupKey, {
        sku: row.sku,
        fnsku: row.fnsku,
        asin: row.asin,
        name: row.name,
        parentJan: row.jan,
        setCount: row.setCount,
        unitCount: row.unitCount,
        memo: row.memo,
        children: groups.get(groupKey)?.children ?? [],
      });
    } else {
      const child: CsvChildRow = {
        jan: row.jan,
        setCount: row.setCount,
        unitCount: row.unitCount,
        name: row.name,
      };
      if (groups.has(groupKey)) {
        groups.get(groupKey)!.children.push(child);
      } else {
        // 親が未到達
        groups.set(groupKey, {
          sku: row.sku,
          fnsku: row.fnsku,
          asin: row.asin,
          name: row.name,
          parentJan: "0",
          setCount: row.setCount,
          unitCount: row.unitCount,
          memo: row.memo,
          children: [child],
        });
      }
    }
  }

  return {
    groupedRows: Array.from(groups.values()),
    validRows,
    duplicateRows,
  };
};
